import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ControlPanelButton from 'components/atoms/controlPanelButton';
import CallIcon from 'components/atoms/icons/callIcon';
import MessageIcon from 'components/atoms/icons/messageIcon';
import PlusIcon from 'components/atoms/icons/plus';
import SmsIcon from 'components/atoms/icons/smsIcon';
import AppointmentIcon from 'components/atoms/icons/appointmentIcon';
import FileIcon from 'components/atoms/icons/fileIcon';
import OrderConsumableIcon from 'components/atoms/icons/orderConsumableIcon';
import TelemedIcon from 'components/atoms/icons/telemedIcon';
import GeneralCommentModal from 'components/templates/rpm/patients/patient/generalComments/modal';
import SmsModal from 'components/templates/rpm/patients/patient/messages/modal';
import SmsModalMobile from 'components/templates/rpm/patients/patient/messages/modalMobile';
import DoctorSmsModal from 'components/organisms/controlPanel/smsDoctorModal';
import OrderConsumableModal from 'components/organisms/controlPanel/orderConsumableModal';
import forms from 'constants/forms';
import { showResult, startTelemed } from 'utils';
import { isMobile } from 'utils/deviceHelper';
import {
  isPractice,
  isAgencyDoctor,
  isBiller,
  isAdmin,
} from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import HospitalIcon from 'components/atoms/icons/hospitalIcon';
import ClinicIcon from 'components/atoms/icons/clinicIcon';
import SuccessPopup from 'components/organisms/successPopup';
import PatientHoldingStatus from 'components/atoms/patientStatus/patientHoldingStatus';
import ControlPanelButtonDropdown from 'components/atoms/controlPanelButton/dropdown';
import useCacheValue from 'utils/useCacheValue';
import useCallButtons from 'utils/hooks/rpm/controlPanel/useCallButtons';
import useSmsButtons from 'utils/hooks/rpm/controlPanel/useSmsButtons';
import useRequestButtons from 'utils/hooks/rpm/controlPanel/useRequestsButtons';
import InteractionStatusButton from 'components/molecules/controlPanel/interactionStatusButton';
import ManualInteractionTimeModal from 'components/organisms/controlPanel/manualInteractionTimeModal';
import useCommentButtons from 'utils/hooks/rpm/controlPanel/useCommentButtons';
import PcctModal from 'components/templates/rpm/patients/patient/generalComments/pcctModal';
import DischargePatientModal from './dischargePatientModal';
import DischargePatientModalMobile from './dischargePatientModalMobile';
import ManualEnterCall from './manualEnteredCallModal';

const ControlPanel = ({
  patientGuid,
  profile,
  doctor,
  patientDetailsLoading,
  status,
  disableControlPanelButtons = false,
  isRpmPanel,
  isCcmPanel,
  isAdminPanel,
}) => {
  const {
    getTemplates: smsTemplatesLoading,
    submit: messageSubmitLoading,
    submitDoctorMessage: doctorMessageSubmitLoading,
  } = useSelector((state) => state.loading.effects.sms);

  const orderConsumableSubmitLoading = useSelector(
    (state) => state.loading.effects.orderConsumable.submit,
  );

  const appointmentSubmitLoading = useSelector(
    (state) => state.loading.effects.appointment.submit,
  );
  const dischargeSubmitLoading = useSelector(
    (state) => state.loading.effects.discharge.submit,
  );
  const ccmDischargeSubmitLoading = useSelector(
    (state) => state.loading.effects.ccmPatients.setCcmStatus,
  );
  const commentSubmitLoading = useSelector(
    (state) => state.loading.effects.comments.submit,
  );
  const clinicalEscalationSubmitLoading = useSelector(
    (state) => state.loading.effects.clinicalEscalation.submit,
  );

  const hospitalPreventionSubmitLoading = useSelector(
    (state) => state.loading.effects.hospitalPrevention.submit,
  );

  const manualInteractionSubmitLoading = useSelector(
    (state) => state.loading.effects.manualInteractiveTime.submit,
  );

  const {
    togglePatientInteractionStatus: updatePatientInteractionStatusLoading,
    togglePatientCcmInteractionStatus: updatePatientCcmInteractionStatusLoading,
    getPatient: getPatientLoading,
  } = useSelector((state) => state.loading.effects.adminPanelPatients);

  const smsTemplates = useSelector((state) => state.sms.templates);
  const device = useSelector((state) => state.user.deviceType);
  const { userType, roleNames } = useUser();

  const patient = useSelector((state) => state.adminPanelPatients.patient);

  const {
    sms: {
      getTemplates: getSmsTemplates,
      submit: submitSms,
      submitDoctorMessage: submitDoctorSms,
    },
    comments: { submit: submitComment },
    discharge: { submit: submitDischarge },
    appointment: { submit: submitAppointment },
    orderConsumable: { submit: submitOrderConsumable },
    clinicalEscalation: { submit: submitClinicalEscalation },
    hospitalPrevention: { submit: submitHospitalPrevention },
    adminPanelPatients: {
      getPatient,
      togglePatientInteractionStatus,
      togglePatientCcmInteractionStatus,
    },
    manualInteractiveTime: { submit: submitManualInteraction },
    ccmPatients: { setCcmStatus },
    ccmPatientDetails: { getPatientDetails },
  } = useDispatch();

  const unenrollButtonDisableStatuses = [
    {
      id: 'ccm_onboarding',
      label: 'CCM Onboarding',
    },
    ...forms.patientCcmDischargeStatuses,
  ];

  const checkIsUnenrolled = (ccmStatus) =>
    unenrollButtonDisableStatuses.some((item) => item.id === ccmStatus);

  const [pcctValues, setPcctValues] = useState({});
  const [comment, setComment] = useState();
  const [successPopup, setSuccessPopup] = useState(false);
  const [commentsPopupOpen, setCommentsPopupOpen] = useState(false);
  const [pcctPopupOpen, setPcctPopupOpen] = useState(false);
  const [smsPopupOpen, setSmsPopupOpen] = useState(false);
  const [dischargePopupOpen, setDischargePopupOpen] = useState(false);
  const [CCMDischargePopupOpen, setCCMDischargePopupOpen] = useState(false);
  const [doctorSmsPopupOpen, setDoctorSmsPopupOpen] = useState(false);
  const [orderConsumablesPopupOpen, setOrderConsumablePopupOpen] =
    useState(false);
  const [manualInteractionsPopupOpen, setManualInteractionsPopupOpen] =
    useState(false);
  const [manualEnteredCallPopupOpen, setManualEnteredCallPopupOpen] =
    useState(false);

  const cacheId = `pcctComment-${patientGuid}`;

  const { cacheLoading } = useCacheValue({
    value: pcctValues,
    setValue: setPcctValues,
    cacheId,
  });

  useEffect(async () => {
    setPcctValues({});
    setPcctPopupOpen(false);
    getSmsTemplates();
    await getPatient({ patientGuid });
  }, [patientGuid]);

  const sendDischargeRequest = async (postData) => {
    const { error } = await submitDischarge(postData);
    await getPatient({ patientGuid });
    return { error };
  };

  const sendCcmDischargeRequest = async (postData) => {
    const { error } = await setCcmStatus(postData);
    setCCMDischargePopupOpen(false);
    await getPatient({ patientGuid });
    await getPatientDetails(patientGuid);
    return { error };
  };

  const sendAppointmentRequest = async () => {
    const postData = {
      patientGuid,
    };

    const { error } = await submitAppointment(postData);
    showResult(error);
  };

  const sendClinicalEscalationRequest = async () => {
    const postData = {
      patientGuid,
    };

    const { error } = await submitClinicalEscalation(postData);
    if (error) showResult(error);
    else setSuccessPopup(true);
  };

  const sendHospitalPreventionRequest = async () => {
    const postData = {
      patientGuid,
    };

    const { error } = await submitHospitalPrevention(postData);
    if (error) showResult(error);
    else setSuccessPopup(true);
  };

  const updateInteractionStatus = async (type) => {
    let res = null;
    if (type === 'rpm') {
      res = await togglePatientInteractionStatus({
        patientGuid,
        isInteracted: !patient.profile.interactionStatus,
      });
    } else {
      res = await togglePatientCcmInteractionStatus({
        patientGuid,
        isInteracted: !patient.profile.ccmInteractionStatus,
      });
    }
    if (res?.error) showResult(res.error);
  };

  const callButtons = useCallButtons({
    doctor,
    patient,
    profile,
    userType,
    isCcmPanel,
    isRpmPanel,
    patientGuid,
    patientDetailsLoading,
    setManualEnteredCallPopupOpen,
  });

  const smsButtons = useSmsButtons({
    doctor,
    userType,
    setSmsPopupOpen,
    setDoctorSmsPopupOpen,
  });

  const requestButtons = useRequestButtons({
    profile,
    patient,
    roleNames,
    isRpmPanel,
    patientGuid,
    sendAppointmentRequest,
    setDischargePopupOpen,
    getPatientLoading,
    patientDetailsLoading,
    dischargeSubmitLoading,
    appointmentSubmitLoading,
  });

  const commentButtons = useCommentButtons({
    setPcctPopupOpen,
    setCommentsPopupOpen,
  });

  return (
    <div className="control-panel">
      <h3>Control Panel</h3>
      <div className="control-panel-body">
        {!isMobile(device) && (
          <>
            <div className="control-panel-buttons">
              <ControlPanelButtonDropdown
                label="Call"
                icon={CallIcon}
                buttons={callButtons}
                disabled={disableControlPanelButtons}
              />
              <ControlPanelButtonDropdown
                label="SMS"
                icon={SmsIcon}
                buttons={smsButtons}
                disabled={disableControlPanelButtons}
              />
              <ControlPanelButtonDropdown
                label="Requests"
                icon={FileIcon}
                buttons={requestButtons}
                disabled={disableControlPanelButtons}
              />
              {isRpmPanel && (
                <ControlPanelButtonDropdown
                  label="General Comment"
                  icon={MessageIcon}
                  buttons={commentButtons}
                />
              )}
              {isCcmPanel && (
                <ControlPanelButton
                  label="General Comment"
                  icon={MessageIcon}
                  handleClick={() => setCommentsPopupOpen(true)}
                  disabled={disableControlPanelButtons}
                />
              )}
              {!isRpmPanel && (
                <div className="discharge">
                  <ControlPanelButton
                    label={
                      patient.ccmStatus !== 'enrolled'
                        ? 'Unenroll Patient'
                        : 'Patient Unenrolled'
                    }
                    icon={FileIcon}
                    handleClick={() => setCCMDischargePopupOpen(true)}
                    disabled={
                      checkIsUnenrolled(patient?.profile?.ccmStatus) ||
                      disableControlPanelButtons
                    }
                  />
                  {isMobile(device) && (
                    <DischargePatientModalMobile
                      isOpen={CCMDischargePopupOpen}
                      toggle={setCCMDischargePopupOpen}
                      patientGuid={patientGuid}
                      options={forms.patientCcmDischargeStatuses}
                      loading={ccmDischargeSubmitLoading || getPatientLoading}
                      submit={sendCcmDischargeRequest}
                      modalType="ccm"
                    />
                  )}
                  {!isMobile(device) && (
                    <DischargePatientModal
                      isOpen={CCMDischargePopupOpen}
                      toggle={setCCMDischargePopupOpen}
                      patientGuid={patientGuid}
                      options={forms.patientCcmDischargeStatuses}
                      loading={ccmDischargeSubmitLoading || getPatientLoading}
                      submit={sendCcmDischargeRequest}
                      modalType="ccm"
                    />
                  )}
                </div>
              )}
              {isRpmPanel && (
                <ControlPanelButton
                  label="Order Consumable"
                  icon={OrderConsumableIcon}
                  handleClick={() => setOrderConsumablePopupOpen(true)}
                  disabled={
                    patientDetailsLoading ||
                    orderConsumableSubmitLoading ||
                    status === 'onboarding' ||
                    forms.patientDischargeStatuses.some(
                      (el) => el.id === status,
                    )
                  }
                />
              )}
              {isRpmPanel && isAgencyDoctor(userType) && (
                <>
                  <ControlPanelButton
                    label="Hospital Prevention"
                    icon={HospitalIcon}
                    disabled={hospitalPreventionSubmitLoading}
                    handleClick={sendHospitalPreventionRequest}
                  />
                  <ControlPanelButton
                    label="Clinical Escalation"
                    icon={ClinicIcon}
                    disabled={clinicalEscalationSubmitLoading}
                    handleClick={sendClinicalEscalationRequest}
                  />
                </>
              )}
              {!isBiller(roleNames) && (
                <PatientHoldingStatus
                  patientGuid={patientGuid}
                  holdingStatus={profile.holdingStatus}
                  patientDetailsLoading={patientDetailsLoading}
                  disabled={disableControlPanelButtons}
                  isCcmPage={isCcmPanel}
                />
              )}
              {isRpmPanel && (
                <InteractionStatusButton
                  status={patient.profile?.interactionStatus}
                  loading={
                    updatePatientInteractionStatusLoading ||
                    patientDetailsLoading
                  }
                  handleClick={() => updateInteractionStatus('rpm')}
                />
              )}
              {!isRpmPanel && (
                <InteractionStatusButton
                  label="Follow-up/Appt Complete"
                  status={patient.profile?.ccmInteractionStatus}
                  loading={
                    updatePatientCcmInteractionStatusLoading ||
                    patientDetailsLoading
                  }
                  disabled={disableControlPanelButtons}
                  handleClick={() => updateInteractionStatus('ccm')}
                />
              )}
              {(isPractice(userType) || isAdmin(userType)) && (
                <ControlPanelButton
                  disabled={disableControlPanelButtons}
                  label="Add Interactive Time"
                  icon={PlusIcon}
                  handleClick={() => setManualInteractionsPopupOpen(true)}
                />
              )}
            </div>
          </>
        )}
        {isMobile(device) && (
          <div className="control-panel-buttons">
            <ControlPanelButton
              label="SMS Patient"
              icon={SmsIcon}
              handleClick={() => setSmsPopupOpen(true)}
              disabled={disableControlPanelButtons}
            />
            {doctor.mobileNumber && !isPractice(userType) && (
              <ControlPanelButton
                label="SMS Primary Doctor"
                icon={SmsIcon}
                handleClick={() => setDoctorSmsPopupOpen(true)}
                disabled={disableControlPanelButtons}
              />
            )}
            <ControlPanelButton
              label="General Comment"
              icon={MessageIcon}
              handleClick={() => setCommentsPopupOpen(true)}
              disabled={disableControlPanelButtons}
            />
            <ControlPanelButton
              label="Appointment Requested"
              icon={AppointmentIcon}
              handleClick={() => sendAppointmentRequest()}
              disabled={
                disableControlPanelButtons ||
                patientDetailsLoading ||
                !profile.appointmentsFlagActive ||
                appointmentSubmitLoading
              }
            />
            <ControlPanelButton
              label="Discharge Request"
              icon={FileIcon}
              handleClick={() => setDischargePopupOpen(true)}
              disabled={
                patientDetailsLoading ||
                dischargeSubmitLoading ||
                disableControlPanelButtons
              }
            />
            <ControlPanelButton
              label="Order Consumable"
              icon={OrderConsumableIcon}
              handleClick={() => setOrderConsumablePopupOpen(true)}
              disabled={
                disableControlPanelButtons ||
                patientDetailsLoading ||
                orderConsumableSubmitLoading ||
                status === 'onboarding' ||
                forms.patientDischargeStatuses.some((el) => el.id === status)
              }
            />
            <ControlPanelButton
              disabled={disableControlPanelButtons}
              label="Start Telemed"
              icon={TelemedIcon}
              handleClick={() => startTelemed()}
            />
            {isRpmPanel && isAgencyDoctor(userType) && (
              <>
                <ControlPanelButton
                  label="Hospital Prevention"
                  icon={HospitalIcon}
                  disabled={hospitalPreventionSubmitLoading}
                  handleClick={sendHospitalPreventionRequest}
                />
                <ControlPanelButton
                  label="Clinical Escalation"
                  icon={ClinicIcon}
                  disabled={clinicalEscalationSubmitLoading}
                  handleClick={sendClinicalEscalationRequest}
                />
              </>
            )}
            {!isBiller(roleNames) && (
              <PatientHoldingStatus
                patientGuid={patientGuid}
                holdingStatus={profile.holdingStatus}
                patientDetailsLoading={patientDetailsLoading}
                disabled={disableControlPanelButtons}
              />
            )}
            {isRpmPanel && (
              <InteractionStatusButton
                status={patient.profile?.interactionStatus}
                loading={
                  updatePatientInteractionStatusLoading || patientDetailsLoading
                }
                handleClick={() => updateInteractionStatus('rpm')}
              />
            )}
            {!isRpmPanel && (
              <InteractionStatusButton
                status={patient.profile?.ccmInteractionStatus}
                loading={
                  updatePatientCcmInteractionStatusLoading ||
                  patientDetailsLoading
                }
                disabled={!disableControlPanelButtons}
                handleClick={() => updateInteractionStatus('ccm')}
              />
            )}
            {(isPractice(userType) || isAdmin(userType)) && (
              <ControlPanelButton
                disabled={disableControlPanelButtons}
                label="Add Interactive Time"
                icon={MessageIcon}
                handleClick={() => setManualInteractionsPopupOpen(true)}
              />
            )}
          </div>
        )}
      </div>
      <GeneralCommentModal
        isOpen={commentsPopupOpen}
        toggle={setCommentsPopupOpen}
        patientGuid={patientGuid}
        loading={commentSubmitLoading}
        submit={submitComment}
        comment={comment}
        resetPcctValue={() => setPcctValues({})}
        resetCommentValue={() => setComment('')}
        isRpmPanel={isRpmPanel}
        isCcmPanel={isCcmPanel}
        isAdminPanel={isAdminPanel}
      />
      {pcctPopupOpen && (
        <PcctModal
          isOpen={pcctPopupOpen}
          closeModal={() => setPcctPopupOpen(false)}
          values={pcctValues}
          setValues={setPcctValues}
          setComment={setComment}
          setCommentsPopupOpen={setCommentsPopupOpen}
          loading={cacheLoading}
        />
      )}
      <SuccessPopup isOpen={successPopup} toggle={setSuccessPopup} />
      {isMobile(device) && (
        <SmsModalMobile
          isOpen={smsPopupOpen}
          toggle={setSmsPopupOpen}
          patientGuid={patientGuid}
          smsTemplates={smsTemplates}
          loading={smsTemplatesLoading || messageSubmitLoading}
          submit={submitSms}
          isRpmPanel={isRpmPanel}
        />
      )}
      {!isMobile(device) && (
        <SmsModal
          isOpen={smsPopupOpen}
          toggle={setSmsPopupOpen}
          patientGuid={patientGuid}
          smsTemplates={smsTemplates}
          loading={smsTemplatesLoading || messageSubmitLoading}
          submit={submitSms}
          isRpmPanel={isRpmPanel}
        />
      )}
      {isMobile(device) && (
        <DischargePatientModalMobile
          isOpen={dischargePopupOpen}
          toggle={setDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientDischargeStatuses}
          loading={dischargeSubmitLoading || getPatientLoading}
          submit={sendDischargeRequest}
        />
      )}
      {!isMobile(device) && (
        <DischargePatientModal
          isOpen={dischargePopupOpen}
          toggle={setDischargePopupOpen}
          patientGuid={patientGuid}
          options={forms.patientDischargeStatuses}
          loading={dischargeSubmitLoading || getPatientLoading}
          submit={sendDischargeRequest}
        />
      )}
      <DoctorSmsModal
        isOpen={doctorSmsPopupOpen}
        toggle={setDoctorSmsPopupOpen}
        patientGuid={patientGuid}
        loading={doctorMessageSubmitLoading}
        submit={submitDoctorSms}
      />
      <OrderConsumableModal
        isOpen={orderConsumablesPopupOpen}
        toggle={setOrderConsumablePopupOpen}
        patientGuid={patientGuid}
        loading={orderConsumableSubmitLoading}
        submit={submitOrderConsumable}
      />
      <ManualInteractionTimeModal
        isOpen={manualInteractionsPopupOpen}
        toggle={setManualInteractionsPopupOpen}
        patientGuid={patientGuid}
        isRpmPanel={isRpmPanel}
        loading={manualInteractionSubmitLoading}
        submit={submitManualInteraction}
      />
      <ManualEnterCall
        isOpen={manualEnteredCallPopupOpen}
        toggle={setManualEnteredCallPopupOpen}
        patientGuid={patientGuid}
        isRpmPanel={isRpmPanel}
      />
    </div>
  );
};

ControlPanel.propTypes = {
  patientGuid: PropTypes.string.isRequired,
  patientDetailsLoading: PropTypes.bool.isRequired,
  profile: PropTypes.shape().isRequired,
  doctor: PropTypes.shape().isRequired,
  status: PropTypes.string,
  disableControlPanelButtons: PropTypes.bool.isRequired,
  isRpmPanel: PropTypes.bool,
  isCcmPanel: PropTypes.bool,
  isAdminPanel: PropTypes.bool,
};

export default ControlPanel;
